/**
 * Продвинутый процессор DOCX с извлечением максимального количества стилей
 */
import mammoth from 'mammoth';

// Стандартные шрифты редактора
const STANDARD_FONTS = [
  'Arial', 'Times New Roman', 'Calibri', 'Georgia', 'Verdana', 
  'Tahoma', 'Helvetica', 'Comic Sans MS', 'Courier New', 'Impact'
];

/**
 * Улучшенная конвертация DOCX с максимальным извлечением стилей
 * @param {ArrayBuffer} arrayBuffer - Буфер DOCX файла
 * @returns {Promise<string>} - Обработанный HTML
 */
export async function processAdvancedDocx(arrayBuffer) {
  try {
    // Сначала получаем raw результат для анализа
    const rawResult = await mammoth.convertToHtml({ arrayBuffer }, {
      includeDefaultStyleMap: true,
      includeEmbeddedStyleMap: true,
      preserveEmptyParagraphs: true,
      
      // Расширенная карта стилей для максимального извлечения информации
      styleMap: [
        // Сохраняем все runs со всеми атрибутами
        "r => span:fresh",
        "p => p:fresh", 
        
        // Заголовки
        "p[style-name='Heading 1'] => h1:fresh",
        "p[style-name='Heading 2'] => h2:fresh", 
        "p[style-name='Heading 3'] => h3:fresh",
        
        // Выравнивание
        "p[style-name='Centered'] => p.text-center:fresh",
        "p[style-name='Right'] => p.text-right:fresh",
        "p[style-name='Justified'] => p.text-justify:fresh",
        
        // Таблицы с максимальной информацией
        "table => table:fresh",
        "tr => tr:fresh",
        "td => td:fresh",
        "th => th:fresh"
      ],
      
      // Кастомная функция трансформации для извлечения атрибутов
      transformDocument: mammoth.transforms.paragraph(function(element) {
        // Извлекаем все доступные атрибуты
        const attributes = {};
        
        if (element.alignment) {
          attributes['data-alignment'] = element.alignment;
        }
        
        if (element.indent && element.indent.left) {
          attributes['data-indent-left'] = element.indent.left;
        }
        
        if (element.indent && element.indent.right) {
          attributes['data-indent-right'] = element.indent.right;
        }
        
        if (element.spacing && element.spacing.before) {
          attributes['data-spacing-before'] = element.spacing.before;
        }
        
        if (element.spacing && element.spacing.after) {
          attributes['data-spacing-after'] = element.spacing.after;
        }
        
        return {
          ...element,
          attributes: attributes
        };
      })
    });
    
    
    let html = rawResult.value;
    
    // Применяем глубокую обработку стилей
    html = await processRunStyles(html, arrayBuffer);
    html = processTableStyles(html);
    html = processAlignmentAndIndents(html);
    html = processFontStyles(html);
    
    return html;
    
  } catch (error) {
    console.error('Error in advanced DOCX processing:', error);
    throw error;
  }
}

/**
 * Обрабатываем стили runs (отдельных фрагментов текста)
 * @param {string} html - HTML строка
 * @param {ArrayBuffer} arrayBuffer - Оригинальный DOCX для дополнительного анализа
 * @returns {Promise<string>} - Обработанный HTML
 */
async function processRunStyles(html, arrayBuffer) {
  // Пытаемся получить больше информации через extractRawText
  try {
    await mammoth.extractRawText({ arrayBuffer });
  } catch (e) {
    console.warn('Could not extract raw text:', e);
  }
  
  // Пытаемся получить информацию о стилях документа
  try {
    const stylesResult = await mammoth.convertToHtml({ arrayBuffer }, {
      styleMap: [
        // Попытка извлечь информацию о шрифтах
        "r[style-name*='Arial'] => span.font-arial:fresh",
        "r[style-name*='Times'] => span.font-times:fresh",
        "r[style-name*='Calibri'] => span.font-calibri:fresh",
        
        // Попытка извлечь размеры
        "r[style-name*='12'] => span.size-12:fresh",
        "r[style-name*='14'] => span.size-14:fresh", 
        "r[style-name*='16'] => span.size-16:fresh",
        "r[style-name*='18'] => span.size-18:fresh",
        "r[style-name*='20'] => span.size-20:fresh",
        "r[style-name*='24'] => span.size-24:fresh",
        
        // Сохраняем все другие атрибуты
        "r => span:fresh"
      ]
    });
    
    if (stylesResult.value && stylesResult.value !== html) {
      // Можем использовать эту информацию для улучшения
    }
  } catch (e) {
    console.warn('Could not extract alternative styles:', e);
  }
  
  // Обрабатываем spans с попыткой извлечения шрифтов из классов
  html = html.replace(/<span([^>]*class="([^"]*)"[^>]*)>([^<]*)<\/span>/g, (match, attributes, className, content) => {
    let style = '';
    
    // Пытаемся извлечь информацию о шрифте из класса
    if (className.includes('font-arial')) {
      style += 'font-family: Arial; ';
    } else if (className.includes('font-times')) {
      style += 'font-family: "Times New Roman"; ';
    } else if (className.includes('font-calibri')) {
      style += 'font-family: Calibri; ';
    }
    
    // Пытаемся извлечь размер из класса
    const sizeMatch = className.match(/size-(\d+)/);
    if (sizeMatch) {
      style += `font-size: ${sizeMatch[1]}px; `;
    }
    
    if (style) {
      return `<span${attributes} style="${style}">${content}</span>`;
    }
    
    return match;
  });
  
  return html;
}

/**
 * Обрабатываем стили таблиц с более точным извлечением размеров
 * @param {string} html - HTML строка
 * @returns {string} - Обработанный HTML
 */
function processTableStyles(html) {
  // Создаем временный DOM для анализа
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const tables = tempDiv.querySelectorAll('table');
  tables.forEach(table => {
    // Применяем базовые стили
    table.style.borderCollapse = 'collapse';
    table.style.width = '100%';
    table.style.margin = '10px 0';
    table.setAttribute('border', '1');
    
    // Анализируем первую строку для определения пропорций колонок
    const firstRow = table.querySelector('tr');
    if (firstRow) {
      const cells = firstRow.querySelectorAll('td, th');
      const totalCells = cells.length;
      
      if (totalCells > 0) {
        cells.forEach((cell, index) => {
          // Устанавливаем равномерную ширину если не указано иное
          if (!cell.style.width) {
            cell.style.width = `${Math.floor(100 / totalCells)}%`;
          }
          
          // Базовые стили для всех ячеек
          cell.style.border = '1px solid #ccc';
          cell.style.padding = '8px';
          cell.style.verticalAlign = 'top';
          
          // Для заголовков
          if (cell.tagName === 'TH') {
            cell.style.backgroundColor = '#f5f5f5';
            cell.style.fontWeight = 'bold';
            cell.style.textAlign = 'center';
          }
          
          // Пытаемся определить выравнивание по содержимому
          const content = cell.textContent.trim();
          
          // Числа - по правому краю
          if (/^\d+([.,]\d+)?$/.test(content) || /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(content)) {
            if (!cell.style.textAlign) {
              cell.style.textAlign = 'right';
            }
          }
          
          // Короткий текст в первой строке - по центру
          if (table.rows[0] === firstRow && content.length < 15 && !/\d/.test(content)) {
            if (!cell.style.textAlign) {
              cell.style.textAlign = 'center';
            }
          }
        });
      }
    }
  });
  
  return tempDiv.innerHTML;
}

/**
 * Обрабатываем выравнивание и отступы
 * @param {string} html - HTML строка  
 * @returns {string} - Обработанный HTML
 */
function processAlignmentAndIndents(html) {
  // Обрабатываем data-атрибуты, добавленные при трансформации
  html = html.replace(/<p([^>]*data-alignment="([^"]*)"[^>]*)>/g, (match, attributes, alignment) => {
    let style = '';
    
    switch (alignment) {
      case 'center':
      case 'Centre':
        style += 'text-align: center; ';
        break;
      case 'right':
      case 'Right':
        style += 'text-align: right; ';
        break;
      case 'justify':
      case 'Justify':
        style += 'text-align: justify; ';
        break;
      case 'left':
      case 'Left':
      default:
        style += 'text-align: left; ';
        break;
    }
    
    return `<p${attributes} style="${style}">`;
  });
  
  // Обрабатываем отступы
  html = html.replace(/<p([^>]*data-indent-left="([^"]*)"[^>]*)>/g, (match, attributes, indent) => {
    const indentValue = parseInt(indent);
    if (indentValue > 0) {
      const paddingPx = Math.round(indentValue * 0.75); // Примерное преобразование twips в пиксели
      const existingStyle = attributes.match(/style="([^"]*)"/);
      
      if (existingStyle) {
        const newStyle = existingStyle[1] + `padding-left: ${paddingPx}px; `;
        return match.replace(existingStyle[0], `style="${newStyle}"`);
      } else {
        return `<p${attributes} style="padding-left: ${paddingPx}px;">`;
      }
    }
    
    return match;
  });
  
  return html;
}

/**
 * Обрабатываем стили шрифтов
 * @param {string} html - HTML строка
 * @returns {string} - Обработанный HTML  
 */
function processFontStyles(html) {
  // Пытаемся найти и обработать встроенные стили
  html = html.replace(/style="([^"]*)"/g, (match, styles) => {
    let processedStyles = styles;
    
    // Обрабатываем font-family
    if (processedStyles.includes('font-family:')) {
      processedStyles = processedStyles.replace(/font-family:\s*([^;]+);?/g, (fontMatch, fontFamily) => {
        const cleanFont = fontFamily.replace(/['"]/g, '').trim();
        const standardFont = STANDARD_FONTS.find(font => 
          cleanFont.toLowerCase().includes(font.toLowerCase())
        );
        
        if (standardFont) {
          return `font-family: "${standardFont}";`;
        } else {
          return 'font-family: "Times New Roman";';
        }
      });
    }
    
    // Обрабатываем размеры шрифта
    if (processedStyles.includes('font-size:')) {
      processedStyles = processedStyles.replace(/font-size:\s*([^;]+);?/g, (sizeMatch, fontSize) => {
        let pxSize = fontSize;
        
        // Конвертируем pt в px
        if (fontSize.endsWith('pt')) {
          const ptValue = parseFloat(fontSize);
          pxSize = Math.round(ptValue * 1.333) + 'px';
        }
        // Конвертируем числа без единиц в px (считая их как pt)
        else if (!fontSize.includes('px') && !fontSize.includes('em') && !fontSize.includes('%')) {
          const numValue = parseFloat(fontSize);
          if (!isNaN(numValue)) {
            pxSize = Math.round(numValue * 1.333) + 'px';
          }
        }
        
        return `font-size: ${pxSize};`;
      });
    }
    
    return `style="${processedStyles}"`;
  });
  
  // Добавляем базовые стили для абзацев без стилей
  html = html.replace(/<p([^>]*(?!style=)[^>]*)>/g, '<p$1 style="margin: 6px 0; line-height: 1.4;">');
  
  return html;
}
